import React, {useCallback, useMemo, useState} from 'react';
import {useTheme} from '@mui/material';
import {assert} from 'assert-ts';
import type {EventNameVariantFieldLayout} from 'components/fields/types';
import type {DataValue, NameVariantValue} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {assertSimpleRequired} from 'schemaDefinition/functions';
import {valueAsNameVariantArray} from 'services/utils';
import {
  CheckboxField,
  Layout,
  SingleValueListField,
  useListOperations,
} from 'components';
import {useUniqueFieldId} from 'components/fields/hooks/useUniqueFieldId';
import type {DataFormNameVariantListProps} from './types';
import {useDataFormContext} from '../contexts';
import {
  useAddItemLabel,
  useEventNameVariantLabelsAndPlaceholders,
  useResolvedDataFormPartConfiguration,
} from '../hooks';
import {EventNameVariant} from './fields';

const nameVariantFieldLayout: EventNameVariantFieldLayout = {
  name: {
    layout: {
      flex: 1,
    },
  },
  description: {
    layout: {
      flex: 1,
    },
  },
  mainForm: {
    layout: {
      flex: 1,
      alignItems: 'flex-end',
    },
  },
};

const newVariant = (withDescription: boolean): NameVariantValue => {
  const value: NameVariantValue = {};
  if (withDescription) {
    value.description = '';
  }
  return value;
};

const hasAnyDescription = (value: DataValue): boolean => {
  const nameVariants = valueAsNameVariantArray(value);

  if (!nameVariants) {
    return false;
  }

  return nameVariants.some(v => v.description);
};

export const DataFormEventNameVariantList: React.FC<
  DataFormNameVariantListProps
> = ({part, useValue, valuePath, mode}) => {
  const {colors} = useTheme();
  const {t} = useLocalization();
  const {id, setEditValue} = useDataFormContext();
  const value = (useValue(valuePath, id, part.name) ?? null) as DataValue;

  const {showWhenReadonlyAndEmpty, hideStructureOperations} =
    useResolvedDataFormPartConfiguration(part);

  const [showDescription, setShowDescription] = useState(() =>
    hasAnyDescription(value),
  );

  assert(
    value === null || Array.isArray(value),
    'DataForm: "name variants"-value property must be array (or null)',
    {part: part, contextPath: valuePath, dataScope: value},
  );

  const requiredValue = useMemo(
    () =>
      !value || value.length === 0
        ? [newVariant(showDescription)]
        : (value as NameVariantValue[]),
    [showDescription, value],
  );

  const {onAppendValue, onDeleteValue, onMoveValue} =
    useListOperations<NameVariantValue>(
      update => {
        const nextValue = update(requiredValue);
        setEditValue(valuePath, nextValue, part);
        return nextValue;
      },
      () => newVariant(showDescription),
    );

  const {contentLabels, fieldLayoutWithPlaceholders} =
    useEventNameVariantLabelsAndPlaceholders(
      showDescription ? 'description' : undefined,
      nameVariantFieldLayout,
    );

  const descriptionFieldId = useUniqueFieldId(`${valuePath}.description`);
  const addLabel = useAddItemLabel(part);

  const renderCustomActions = useCallback(() => {
    const handleChangeDescription = (checked: boolean) => {
      const nameVariants = valueAsNameVariantArray(requiredValue);
      setShowDescription(checked);

      // Remove values when hidden. Define description when visible.
      const newValue = checked
        ? nameVariants?.map<NameVariantValue>(nv => ({
            ...nv,
            description: nv.description ?? '',
          }))
        : nameVariants?.map<NameVariantValue>(({description: _, ...nv}) => nv);
      setEditValue(valuePath, newValue ?? null, part);
    };

    return (
      <Layout horizontal>
        <CheckboxField
          fieldId={descriptionFieldId}
          name={`${valuePath}.description`}
          value={showDescription}
          boxLabel={t('page.agents.nameVariants.description')}
          onChange={handleChangeDescription}
        />
      </Layout>
    );
  }, [
    descriptionFieldId,
    valuePath,
    showDescription,
    t,
    requiredValue,
    setEditValue,
    part,
  ]);

  return (
    <SingleValueListField<NameVariantValue>
      addLabel={addLabel}
      label={'.'}
      itemBackgroundColor={colors.enhancedLightBeige}
      itemPadding={1}
      contentLabels={contentLabels}
      required={assertSimpleRequired(part.required)}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      values={requiredValue}
      getValueKey={idx => `${valuePath}[${idx}]`}
      renderValue={(idx, value, focusableId) => {
        return (
          <EventNameVariant
            focusableId={focusableId}
            part={part}
            value={value as NameVariantValue}
            valuePath={`${valuePath}[${idx}]`}
            setFieldValue={setEditValue}
            isMainForm={idx === 0}
            mode={mode}
            showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
            layout={fieldLayoutWithPlaceholders}
          />
        );
      }}
      renderCustomActions={renderCustomActions}
      onAppendValue={
        hideStructureOperations?.includes('add') ? undefined : onAppendValue
      }
      onDeleteValue={
        hideStructureOperations?.includes('delete') ? undefined : onDeleteValue
      }
      onMoveValue={
        hideStructureOperations?.includes('move') ? undefined : onMoveValue
      }
    />
  );
};
